/**
 * ValidationStatusPill — Floating workspace validation status indicator.
 *
 * Positioned top-left of the workspace canvas, shows aggregate validation
 * status across all nodes. Clickable to trigger validate_work.
 *
 * States:
 * - unvalidated: no nodes have been validated yet (grey)
 * - validated: all nodes approved (green)
 * - review_needed: some nodes changed since last validation (amber)
 * - issues_found: validation found problems (red)
 * - validating: validation currently running (blue, spinner)
 * - hidden: no nodes exist
 *
 * Refs #89
 */

import React from 'react';
import { formatCount } from '../../utils/plural';

export type ValidationState =
  | 'unvalidated'
  | 'validated'
  | 'review_needed'
  | 'issues_found'
  | 'validating'
  | 'hidden';

interface ValidationStatusPillProps {
  state: ValidationState;
  issueCount?: number;
  nodeCount?: number;
  approvedCount?: number;
  onClick?: () => void;
}

const STATE_CONFIG: Record<
  Exclude<ValidationState, 'hidden'>,
  { icon: React.ReactNode; label: string; className: string; tooltip: string }
> = {
  unvalidated: {
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" />
      </svg>
    ),
    label: 'Unvalidated',
    className: 'text-fg-tertiary bg-surface/90 border-border-default/60 hover:bg-surface',
    tooltip: 'Click to validate',
  },
  validating: {
    icon: (
      <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" d="M12 3a9 9 0 019 9" />
      </svg>
    ),
    label: 'Validating\u2026',
    className: 'text-accent bg-accent-tint/90 border-accent-border/60',
    tooltip: 'Validation in progress',
  },
  validated: {
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    ),
    label: 'Validated',
    className: 'text-success bg-success-bg/90 border-success/60 hover:bg-success-bg',
    tooltip: 'All nodes approved \u2014 click to re-validate',
  },
  review_needed: {
    icon: (
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="6" />
      </svg>
    ),
    label: 'Review needed',
    className: 'text-warning bg-warning-bg/90 border-warning/60 hover:bg-warning-bg',
    tooltip: 'Nodes changed since last validation \u2014 click to re-validate',
  },
  issues_found: {
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
    label: 'Issues found',
    className: 'text-danger bg-danger-bg/90 border-danger/60 hover:bg-danger-bg',
    tooltip: 'Validation found issues \u2014 click to re-validate',
  },
};

export default function ValidationStatusPill({
  state,
  issueCount,
  nodeCount,
  approvedCount,
  onClick,
}: ValidationStatusPillProps) {
  if (state === 'hidden') return null;

  const config = STATE_CONFIG[state];
  const isClickable = state !== 'validating';

  // Build label with counts where relevant
  let displayLabel = config.label;
  if (state === 'issues_found' && issueCount) {
    displayLabel = formatCount(issueCount, { one: 'issue', other: 'issues' });
  }

  // Build tooltip with details
  let tooltip = config.tooltip;
  if (state === 'validated' && nodeCount) {
    tooltip = `${nodeCount}/${nodeCount} nodes approved \u2014 click to re-validate`;
  } else if (state === 'review_needed' && nodeCount && approvedCount !== undefined) {
    const changed = nodeCount - approvedCount;
    tooltip = `${formatCount(changed, { one: 'node', other: 'nodes' })} changed since last validation \u2014 click to re-validate`;
  }

  return (
    <button
      onClick={isClickable ? onClick : undefined}
      disabled={!isClickable}
      title={tooltip}
      className={`
        absolute top-16 left-4 z-20
        inline-flex items-center gap-1.5
        px-2.5 py-1
        text-xs font-medium
        rounded-full
        backdrop-blur-md
        border
        shadow-sm
        transition-all duration-150
        select-none
        ${isClickable ? 'cursor-pointer' : 'cursor-default'}
        ${config.className}
      `}
    >
      {config.icon}
      {displayLabel}
    </button>
  );
}

/**
 * Compute aggregate validation state from node validation statuses.
 */
export function computeValidationState(
  nodes: Array<{ validation_status?: string | null }>
): { state: ValidationState; issueCount: number; nodeCount: number; approvedCount: number } {
  if (!nodes || nodes.length === 0) {
    return { state: 'hidden', issueCount: 0, nodeCount: 0, approvedCount: 0 };
  }

  let approved = 0;
  let rejected = 0;
  let reviewNeeded = 0;
  let unvalidated = 0;

  for (const node of nodes) {
    switch (node.validation_status) {
      case 'approved':
        approved++;
        break;
      case 'rejected':
        rejected++;
        break;
      case 'review_needed':
        reviewNeeded++;
        break;
      default:
        unvalidated++;
    }
  }

  const total = nodes.length;

  let state: ValidationState;
  if (approved === total) {
    state = 'validated';
  } else if (rejected > 0) {
    state = 'issues_found';
  } else if (reviewNeeded > 0) {
    state = 'review_needed';
  } else {
    state = 'unvalidated';
  }

  return { state, issueCount: rejected, nodeCount: total, approvedCount: approved };
}
